import { Injectable } from '@angular/core';

declare const window: any;

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * CHATBOT ACCESS KEY
 * ─────────────────────────────────────────────────────────────────────────────
 * window.__env.chatbotEnabled must match this EXACT string for the AI
 * Assistant FAB to appear. true / false / any other value = hidden.
 *
 * Share this key ONLY with authorized DevOps personnel.
 * To rotate: change the string here, rebuild & redeploy.
 *
 * How it is used in env.js:
 *   window.__env.chatbotEnabled = 'XEDGE-AI-7F2K-VIAVI-2k26-PROD';  ← enable
 *   window.__env.chatbotEnabled = false;                              ← disable
 * ─────────────────────────────────────────────────────────────────────────────
 */
const CHATBOT_ACCESS_KEY = 'AIassis-_7F2KviAVI2k26PROD274';

/**
 * ChatConfigService
 * ─────────────────
 * Single source-of-truth for every runtime configuration value.
 * All values are read from window.__env which is loaded by env.js
 * BEFORE the Angular bundles — so no rebuild is needed to reconfigure.
 */
@Injectable()
export class ChatConfigService {

  // ── Backend URL ────────────────────────────────────────────────────────────

  get backendUrl(): string {
    const raw: string = window?.__env?.chatbotUrl;

    // Mixed-content guard:
    // Auto-upgrade http:// → https:// when the page is served over HTTPS
    // so browsers never block the API calls as mixed content.
    if (
      typeof window !== 'undefined' &&
      window.location.protocol === 'https:' &&
      raw.startsWith('http:')
    ) {
      return raw.replace(/^http:\/\//, 'https://');
    }

    return raw;
  }

  // ── Auth ──────────────────────────────────────────────────────────────────

  get kuzzleToken(): string {
    return localStorage.getItem('token') || '';
  }

  /**
   * Optional MCP HTTP adapter token.
   * Required only when MCP_HTTP_TOKEN is set on the adapter.
   * Stored in localStorage under 'mcp_token' — leave blank if not used.
   */
  get mcpToken(): string {
    return localStorage.getItem('mcp_token') || '';
  }

  // ── Controller ────────────────────────────────────────────────────────────

  get controllerId(): string {
    return localStorage.getItem('chat_controller_id') || 'dev';
  }

  set controllerId(val: string) {
    localStorage.setItem('chat_controller_id', val);
  }

  // ── Feature flag ──────────────────────────────────────────────────────────

  /**
   * isChatbotEnabled
   *
   * Returns true ONLY when window.__env.chatbotEnabled contains the exact
   * access key defined in CHATBOT_ACCESS_KEY above.
   *
   * Anything else — false, 'true', missing, wrong string — returns false.
   * This means a misconfigured or default env.js NEVER accidentally enables
   * the feature (safe opt-in by design).
   *
   * env.js      │ result
   * ────────────┼────────────
   * (key)       │ ✅ visible
   * false       │ 🚫 hidden
   * true        │ 🚫 hidden  ← old format no longer works
   * missing     │ 🚫 hidden
   * wrong value │ 🚫 hidden
   */
  get isChatbotEnabled(): boolean {
    const flag = window?.__env?.chatbotEnabled;
    return typeof flag === 'string' && flag.trim() === CHATBOT_ACCESS_KEY;
  }

  get isConfigured(): boolean {
    return !!this.backendUrl && !!this.kuzzleToken;
  }

  // ── HTTP header builders ──────────────────────────────────────────────────

  buildHeaders(): Record<string, string> {
    const headerKey = window?.__env?.chatbotHeader || 'X-Kuzzle-Token';
    const h: Record<string, string> = {
      'Content-Type':    'application/json',
      [headerKey]:  this.kuzzleToken,
      'X-Controller-Id': this.controllerId,
    };
    const mcp = this.mcpToken;
    if (mcp) h['X-MCP-Token'] = mcp;
    return h;
  }

  buildGetHeaders(): Record<string, string> {
    const headerKey = window?.__env?.chatbotHeader || 'X-Kuzzle-Token';
    const h: Record<string, string> = {
      [headerKey]:  this.kuzzleToken,
      'X-Controller-Id': this.controllerId,
    };
    const mcp = this.mcpToken;
    if (mcp) h['X-MCP-Token'] = mcp;
    return h;
  }

  // ── Controller auto-resolution ────────────────────────────────────────────

  async resolveControllerId(): Promise<string> {
    try {
      const res = await fetch(`${this.backendUrl}/api/controllers`);
      if (!res.ok) throw new Error('controllers fetch failed');

      const data: { controllers: { id: string; name: string; [key: string]: string }[] }
        = await res.json();

      const hostField = window?.__env?.chatbotControllerHost || 'xedge_host';
      const currentHost = window.location.hostname.toLowerCase();

      const getHost = (c: { [key: string]: string }): string =>
      (c[hostField] ?? c['xedge_host'] ?? c['kuzzle_host'] ?? '').toLowerCase();

      const matched = data.controllers.find(c =>
        getHost(c) === currentHost
      );
      if (matched) {
        this.controllerId = matched.id;
        console.log(`[Chatbot] Controller resolved: ${matched.id} (field: ${hostField})`);
        return matched.id;
      }

      const partial = data.controllers.find(c => {
        const host = getHost(c);
        return (
          currentHost.includes(c.id.toLowerCase()) ||
          currentHost.includes(host.split('.')[0])
        );
      });
      if (partial) {
        this.controllerId = partial.id;
        console.log(`[Chatbot] Controller resolved (partial): ${partial.id} (field: ${hostField})`);
        return partial.id;
      }

      console.warn(`[Chatbot] No controller matched "${currentHost}", using cached: ${this.controllerId}`);
      return this.controllerId;

    } catch (err) {
      console.warn('[Chatbot] Could not resolve controller, using cached:', this.controllerId, err);
      return this.controllerId;
    }
  }
}